/**
 * Common CPSC Validation Helpers
 *
 * Shared validation logic used by both REF and FULL validators.
 */
import type { AdditionalFormError, NamePath } from '@xbcb/ui-types';
import { ValidationErrorType } from '@xbcb/ui-types';
import { CpscItemType } from '__generated__/graphql';
import type { CpscItemFormState, UsCpscFormState } from 'libs/cpsc';

/** Result structure for validation helpers */
export interface ValidationResult {
  validateFields: NamePath[];
  additionalErrors: AdditionalFormError[];
}

/**
 * Create empty validation result.
 * @return {ValidationResult} Empty validation result
 */
export const createValidationResult = (): ValidationResult => ({
  validateFields: [],
  additionalErrors: [],
});

/**
 * Merge multiple validation results into one.
 * @param {...ValidationResult} results - Validation results to merge
 * @return {ValidationResult} Merged validation result
 */
export const mergeValidationResults = (
  ...results: ValidationResult[]
): ValidationResult => ({
  validateFields: results.flatMap((r) => r.validateFields),
  additionalErrors: results.flatMap((r) => r.additionalErrors),
});

/**
 * Validate items array cardinality (Beta Pilot constraints).
 *
 * Rules:
 * - At least 1 item required
 * - Beta Pilot: Max 1 item per PGA line
 *
 * @param {CpscItemFormState[]} items - CPSC items array
 * @param {NamePath} cpscPath - Path to CPSC fields in form
 * @return {ValidationResult} Validation result
 */
export const validateItemsCardinality = (
  items: CpscItemFormState[],
  cpscPath: NamePath,
): ValidationResult => {
  const result = createValidationResult();

  // At least one item required
  if (!items.length) {
    result.additionalErrors.push({
      title: 'CPSC Items Required',
      type: ValidationErrorType.GENERAL,
      path: [...cpscPath, 'items'],
      messages: ['At least one item is required'],
    });
  }

  // Beta Pilot: Max 1 item
  if (items.length > 1) {
    result.additionalErrors.push({
      title: 'CPSC Beta Pilot Constraint',
      type: ValidationErrorType.GENERAL,
      path: [...cpscPath, 'items'],
      messages: ['Only one item is allowed during Beta Pilot'],
    });
  }

  return result;
};

/**
 * Validate item type (required + Beta Pilot constraint).
 *
 * Rules:
 * - itemType is required
 * - Beta Pilot: itemType must be PRODUCT
 *
 * @param {CpscItemFormState} item - CPSC item form data
 * @param {NamePath} itemPath - Path to item in form
 * @return {ValidationResult} Validation result
 */
export const validateItemType = (
  item: CpscItemFormState,
  itemPath: NamePath,
): ValidationResult => {
  const result = createValidationResult();

  // itemType required
  if (!item.itemType) {
    result.validateFields.push([...itemPath, 'itemType']);
  }

  // Beta Pilot: itemType must be PRODUCT
  if (item.itemType && item.itemType !== CpscItemType.PRODUCT) {
    result.additionalErrors.push({
      title: 'CPSC Beta Pilot Constraint',
      type: ValidationErrorType.GENERAL,
      path: [...itemPath, 'itemType'],
      messages: ['Item type must be Product during Beta Pilot'],
    });
  }

  return result;
};

/**
 * Validate intended use fields.
 *
 * Rules:
 * - intendedUseDetails.code is required
 * - intendedUseDetails.description required when code = "980.000"
 *
 * @param {UsCpscFormState} pga - CPSC PGA form data
 * @param {NamePath} cpscPath - Path to CPSC fields in form
 * @return {ValidationResult} Validation result
 */
export const validateIntendedUse = (
  pga: UsCpscFormState,
  cpscPath: NamePath,
): ValidationResult => {
  const result = createValidationResult();

  // intendedUseDetails.code is required
  if (!pga?.intendedUseDetails?.code) {
    result.validateFields.push([...cpscPath, 'intendedUseDetails', 'code']);
  }

  // intendedUseDetails.description required when code = "980.000"
  const intendedUseCode = pga?.intendedUseDetails?.code;
  if (intendedUseCode === '980.000' && !pga?.intendedUseDetails?.description) {
    result.validateFields.push([
      ...cpscPath,
      'intendedUseDetails',
      'description',
    ]);
    result.additionalErrors.push({
      title: 'CPSC Intended Use Description Required',
      type: ValidationErrorType.GENERAL,
      path: [...cpscPath, 'intendedUseDetails', 'description'],
      messages: ['Intended use description is required when code is 980.000'],
    });
  }

  return result;
};
